import {
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Form,
  GetRef,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Typography,
  Upload,
} from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  LLMType,
  ProjectData,
  useClient,
} from '../client';
import { AppContext } from '../context';
import { CanceledError } from 'axios';
import LoadingPlaceholder from './LoadingPlaceholder';
import {
  ExportOutlined,
  ImportOutlined,
} from '@ant-design/icons';

const requiredRules = [{ required: true, message: 'Required parameter!' }];

export default function ProjectSettings() {
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const { projectId, projectName, setProjectName, user } =
    useContext(AppContext);
  const [updateGptLimit, setUpdateGptLimit] = useState(false);
  const exportLinkRef = useRef<HTMLAnchorElement>(null);
  const [llmType, setLlmType] = useState<LLMType | null>(null);

  const [importOpen, setImportOpen] = useState(false);

  const client = useClient(projectId);

  const fetchProjectData = () => {
    if (projectId === null) return;
    setFetching(true);
    (async () => {
      try {
        const projData = await client.getProject();
        setProjectData(projData);
        setLlmType(projData.llm_type);
      } catch (error) {
        if (error instanceof CanceledError) return;
        messageApi.error('Error fetching project');
        console.log('Error fetching project:', error);
      }
      setFetching(false);
    })();
  };

  useEffect(fetchProjectData, [projectId, messageApi, client]);

  useEffect(() => {
    if (projectId === null || !projectData || !saving) return;
    (async () => {
      try {
        await client.updateProject(projectData);
        setProjectName(projectData.name);
        messageApi.success('Saved');
      } catch (error) {
        if (error instanceof CanceledError) return;
        messageApi.error('Error updating project');
        console.log('Error updating project:', error);
      }
      setSaving(false);
    })();
  }, [projectId, messageApi, projectData, saving, setProjectName, client]);

  if (!projectData) {
    return (
      <>
        {contextHolder}
        <LoadingPlaceholder />
      </>
    );
  }

  const onExport = async () => {
    if (projectId === null) return;
    const exportData = await client.exportProject();
    if (!exportLinkRef.current) return;
    const exportText = JSON.stringify(exportData);
    const blob = new Blob([exportText], { type: 'application/json' });
    const fileName = `${projectName}-${new Date().toISOString()}.json`;
    const url = window.URL.createObjectURL(blob);
    try {
      exportLinkRef.current.href = url;
      exportLinkRef.current.download = fileName;
      exportLinkRef.current.click();
    } finally {
      window.URL.revokeObjectURL(url);
    }
  };

  const importFromJson = async (jsonData: string) => {
    if (projectId === null || importing) return;
    setImporting(true);
    try {
      await client.importProject(jsonData);
      messageApi.success('Project imported!');
      fetchProjectData();
    } catch (error) {
      if (error instanceof CanceledError) return;
      messageApi.error('Error importing project');
      console.log('Error importing project:', error);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Card loading={fetching || importing}>
        <Flex align="center" vertical>
          <Form
            className="project-settings-form"
            initialValues={projectData}
            onFinish={(newParams: Record<string, any>) => {
              console.log('onFinish');
              setProjectData(newParams as ProjectData);
              setSaving(true);
            }}
          >
            <Form.Item
              key={'param-name'}
              name="name"
              label="Name"
              rules={requiredRules}
              required={false}
            >
              <Input />
            </Form.Item>
            <Form.Item
              key={'param-gpt-key'}
              name="gpt_key"
              label="LLM key"
              required={false}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              key={'param-llm-type'}
              name="llm_type"
              label="LLM type"
              required={false}
            >
              <Select
                onChange={(value) => {
                  setLlmType(value as LLMType);
                }}
              >
                <Select.Option value="openai">openai</Select.Option>
                <Select.Option value="openrouter">openrouter</Select.Option>
                <Select.Option value="custom">custom</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              key={'param-llm-custom-url'}
              name="llm_custom_url"
              label="LLM custom URL"
              required={false}
              hidden={llmType !== 'custom'}
            >
              <Input />
            </Form.Item>
            <Form.Item
              key={'param-llm-model'}
              name="llm_model"
              label="LLM model"
              rules={requiredRules}
              required={false}
            >
              <Input />
            </Form.Item>
            {/* <Form.Item
            key={'param-update-gpt-key-limit'}
            name="update_gpt_key_limit"
            label="Update GPT key limit"
            required={false}
            valuePropName="checked"
          >
            <Checkbox
              checked={updateGptLimit}
              onChange={(e) => {
                setUpdateGptLimit(e.target.checked);
              }}
            />
          </Form.Item>
          <Form.Item
            key={'param-gpt-key-limit'}
            name="gpt_key_limit"
            label="GPT key limit"
            required={false}
          >
            <InputNumber disabled={!updateGptLimit} />
          </Form.Item> */}
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Form.Item>
          </Form>
          {user?.is_admin && (
            <>
              <Divider />
              <div>
                <Button
                  icon={<ExportOutlined />}
                  onClick={onExport}
                  style={{ marginRight: '1em' }}
                >
                  Export
                </Button>
                <a style={{ display: 'none' }} ref={exportLinkRef} />
                <Button
                  icon={<ImportOutlined />}
                  onClick={() => setImportOpen(true)}
                >
                  Import
                </Button>
                <Modal
                  open={importOpen}
                  onCancel={() => setImportOpen(false)}
                  closeIcon={null}
                  footer={null}
                  loading={importing}
                >
                  <Upload.Dragger
                    className="import-dropzone"
                    multiple={false}
                    beforeUpload={async (file) => {
                      await importFromJson(await file.text());
                      setImportOpen(false);
                      return false;
                    }}
                    maxCount={1}
                    fileList={[]}
                  >
                    <Typography.Title level={4}>
                      <ImportOutlined /> Import
                    </Typography.Title>
                  </Upload.Dragger>
                </Modal>
              </div>
            </>
          )}
        </Flex>
      </Card>
    </>
  );
}
